import { UserPlus } from 'lucide-react'
import { useState } from 'react'

import { useCompanies } from '@/features/companies/api'
import { useSession } from '@/features/auth/use-session'
import { PageHeader } from '@/shared/components/common/PageHeader'
import { RelativeTime } from '@/shared/components/common/RelativeTime'
import { UserAvatar } from '@/shared/components/common/UserAvatar'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button, Select, Switch } from '@/shared/components/ui'
import { DEFAULT_LOCALE, LOCALES, LOCALE_NATIVE_NAME, useT, type Locale } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { CompanyListItem, Role } from '@/shared/types/domain'

import { useInviteUser, useSetUserCompanies, useSettingsUsers, useUpdateUser } from './api'
import { InviteUserForm } from './components/InviteUserForm'
import { SettingsNav } from './components/SettingsNav'
import { UserCompaniesSheet } from './components/UserCompaniesSheet'
import { MEMBERSHIPS_UNAVAILABLE_KEY, ROLE_LABEL_KEY, type SettingsUser } from './types'

/**
 * `/settings/usuarios` — quién entra, con qué rol y a qué empresas.
 *
 * Tres reglas que la pantalla hace visibles en vez de esconder en un 422:
 *
 * - **Nadie se edita a sí mismo el rol ni se desactiva.** Sacarse el admin por
 *   accidente deja el sistema sin nadie que pueda devolvérselo.
 * - Un **admin** ve todas las empresas por definición: no tiene lista que
 *   editar.
 * - Un **client** pertenece a exactamente una empresa (hay un CHECK en la base
 *   que lo hace imposible de otra forma), así que tampoco tiene lista.
 */
export function UsersPage() {
  const t = useT()
  const { me } = useSession()
  const users = useSettingsUsers()
  const companies = useCompanies()
  const invite = useInviteUser()
  const update = useUpdateUser()
  const setCompanies = useSetUserCompanies()

  const [editing, setEditing] = useState<SettingsUser | null>(null)
  const [invitando, setInvitando] = useState(false)

  const all = users.data ?? []
  const companyList = companies.data ?? []

  return (
    <div className="flex min-h-dvh flex-col">
      <PageHeader
        title={t('settings.users.title')}
        description={t('settings.users.description')}
        actions={
          <Button variant="primary" onClick={() => setInvitando(true)}>
            <UserPlus size={13} aria-hidden="true" />
            {t('settings.invite.submit')}
          </Button>
        }
      >
        <SettingsNav />
      </PageHeader>

      <div className="flex flex-1 flex-col gap-4 px-4 py-3">
        {users.isPending ? (
          <SkeletonRows rows={5} columns={[0.28, 0.16, 0.12, 0.28]} withSpine={false} />
        ) : users.isError ? (
          <ErrorState error={users.error} onRetry={() => void users.refetch()} />
        ) : all.length === 0 ? (
          <EmptyState
            title={t('settings.users.emptyTitle')}
            description={t('settings.users.emptyDescription')}
          />
        ) : (
          <ul
            aria-label={t('settings.users.listAria')}
            className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
          >
            {/*
              Los encabezados, que no existían.

              Sin ellos la fila era una hilera de controles sueltos: un select
              que decía "Cliente", otro que decía "Português", un texto suelto y
              un interruptor sin nombre. Cada uno era legible por separado y
              ninguno decía QUÉ estaba diciendo — "Nunca entró" puede ser
              cualquier cosa hasta que arriba dice "Último ingreso".
            */}
            <li
              aria-hidden="true"
              className={cn(
                'hairline-b hidden px-3 py-1.5 md:grid',
                COLUMNAS,
                'items-center gap-3 bg-surface-2/40',
              )}
            >
              <span />
              <span className={ENCABEZADO}>{t('settings.users.col.user')}</span>
              <span className={ENCABEZADO}>{t('settings.users.col.role')}</span>
              <span className={ENCABEZADO}>{t('settings.users.col.locale')}</span>
              <span className={ENCABEZADO}>{t('settings.users.col.companies')}</span>
              <span className={ENCABEZADO}>{t('settings.users.col.lastLogin')}</span>
              <span className={ENCABEZADO}>{t('settings.users.col.active')}</span>
              <span />
            </li>

            {all.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                companies={companyList}
                isSelf={user.id === me?.id}
                pending={update.isPending}
                onPatchRole={(role) => update.mutate({ userId: user.id, patch: { role } })}
                onToggleActive={(isActive) =>
                  update.mutate({ userId: user.id, patch: { is_active: isActive } })
                }
                onPatchLocale={(locale) => update.mutate({ userId: user.id, patch: { locale } })}
                onEditCompanies={() => setEditing(user)}
              />
            ))}
          </ul>
        )}
      </div>

      {invitando ? (
        <InviteUserForm
          companies={companyList}
          pending={invite.isPending}
          onInvite={(input) => invite.mutate(input)}
          onClose={() => setInvitando(false)}
        />
      ) : null}

      {editing ? (
        <UserCompaniesSheet
          user={editing}
          companies={companyList}
          pending={setCompanies.isPending}
          onCancel={() => setEditing(null)}
          onSave={(companyIds) => {
            setCompanies.mutate(
              { userId: editing.id, companyIds },
              { onSuccess: () => setEditing(null) },
            )
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Las columnas viven en una constante porque las comparten la cabecera y las
 * filas. Escritas dos veces, la primera que alguien tocara desalinearía los
 * títulos de sus datos, que es peor que no tener títulos.
 */
const COLUMNAS = 'md:grid-cols-[24px_minmax(0,1fr)_150px_120px_210px_110px_28px_92px]' as const

const ENCABEZADO = 'text-2xs tracking-wide text-text-3 uppercase' as const

interface UserRowProps {
  user: SettingsUser
  companies: readonly CompanyListItem[]
  isSelf: boolean
  pending?: boolean
  onPatchRole: (role: Role) => void
  onToggleActive: (isActive: boolean) => void
  onPatchLocale: (locale: Locale) => void
  onEditCompanies: () => void
}

function UserRow({
  user,
  companies,
  isSelf,
  pending,
  onPatchRole,
  onToggleActive,
  onPatchLocale,
  onEditCompanies,
}: UserRowProps) {
  const t = useT()
  // `null` = el backend no informa las membresías en el listado. No es lo
  // mismo que "ninguna": una lista vacía diría que no tiene ninguna empresa
  // asignada, que es una afirmación que no podemos hacer.
  const memberships = user.company_ids
  const names = memberships
    ? companies.filter((company) => memberships.includes(company.id)).map((company) => company.name)
    : null

  return (
    <li
      className={cn(
        'hairline-b items-center gap-3 px-3 py-2.5 last:border-b-0',
        // Grilla, no `flex-wrap`: **las filas del equipo tienen una celda más**
        // (el botón de empresas) y una lista de empresas larga, así que con
        // flex cada fila decidía su propio reparto y las columnas dejaban de
        // alinearse entre vecinas. Peor: el ancho fijo estaba en un `<span>`,
        // que es `inline` y no acepta `width`, así que "Farmacias del Valle ·
        // Grupo Barrantes · Nube Andina" se desbordaba ENCIMA de la columna de
        // al lado y se leía "Nube Andica entró".
        //
        // Las ocho columnas son las mismas para todos; la última queda vacía
        // para quien no es del equipo, que es lo que mantiene el alineado.
        // Ninguna columna mide `auto`: cada fila es su PROPIA grilla, así que
        // una columna que se mide por su contenido da un ancho distinto según
        // la fila tenga o no el botón de empresas — y las columnas de dos
        // vecinas dejan de coincidir aunque cada fila esté bien armada.
        'flex flex-wrap md:grid',
        COLUMNAS,
        user.is_active ? null : 'opacity-60',
      )}
    >
      <UserAvatar user={user} />

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm text-text-1">
          {user.display_name}
          {isSelf ? (
            <span className="ml-1.5 text-2xs text-text-3">{t('settings.users.you')}</span>
          ) : null}
        </span>
        <span className="num truncate text-2xs text-text-3">{user.email}</span>
      </span>

      <Select
        value={user.role}
        disabled={isSelf || pending}
        aria-label={t('settings.users.roleAria', { persona: user.display_name })}
        title={isSelf ? t('settings.users.selfRoleHint') : undefined}
        onChange={(event) => onPatchRole(event.target.value as Role)}
        className="w-[150px] shrink-0"
      >
        {(Object.keys(ROLE_LABEL_KEY) as Role[]).map((role) => (
          <option key={role} value={role}>
            {t(ROLE_LABEL_KEY[role])}
          </option>
        ))}
      </Select>

      {/*
        El idioma del perfil, y **también el de uno mismo**: es lo único que
        alguien puede cambiarse acá. Cada opción se lee en SU idioma —nadie
        busca "Spanish" en un menú en español— y por eso los nombres salen de
        `LOCALE_NATIVE_NAME` y no del catálogo.
      */}
      <Select
        value={user.locale ?? DEFAULT_LOCALE}
        disabled={pending}
        aria-label={t('settings.users.localeAria', { persona: user.display_name })}
        onChange={(event) => onPatchLocale(event.target.value as Locale)}
        className="w-[120px] shrink-0"
      >
        {LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {LOCALE_NATIVE_NAME[locale]}
          </option>
        ))}
      </Select>

      <span className="block w-[210px] min-w-0 shrink-0 truncate text-2xs text-text-2">
        {user.role === 'admin' ? (
          <span className="text-text-3">{t('settings.users.allCompanies')}</span>
        ) : user.role === 'client' ? (
          // Un cliente ve exactamente una empresa. Si no está en la lista es
          // porque se archivó: se dice eso, en vez de "Su empresa" — que era
          // una etiqueta que ocupaba el lugar del dato sin nombrarlo.
          (() => {
            const empresa = companies.find((company) => company.id === user.client_company_id)
            return empresa ? (
              <span className="text-text-2">{empresa.name}</span>
            ) : (
              <span className="text-text-3" title={t('settings.users.unknownCompany')}>
                {t('common.state.none')}
              </span>
            )
          })()
        ) : names === null ? (
          // El listado del backend no informa las membresías. Se dice, en vez
          // de mostrar "Sin empresas asignadas", que sería directamente falso.
          <span className="text-text-3" title={t(MEMBERSHIPS_UNAVAILABLE_KEY)}>
            {t('common.label.unknown')}
          </span>
        ) : (
          <button
            type="button"
            onClick={onEditCompanies}
            // `block w-full`: `truncate` no recorta nada sobre un elemento que
            // se dimensiona por su contenido.
            className="block w-full truncate text-left hover:text-text-1"
            title={names.join(' · ')}
          >
            {names.length > 0 ? (
              names.join(' · ')
            ) : (
              <span className="text-text-3">{t('settings.users.noCompanies')}</span>
            )}
          </button>
        )}
      </span>

      <span className="num block w-[110px] shrink-0 truncate text-2xs text-text-3">
        {user.last_login_at ? (
          <RelativeTime value={user.last_login_at} />
        ) : (
          t('settings.users.neverLoggedIn')
        )}
      </span>

      <Switch
        checked={user.is_active}
        disabled={isSelf || pending}
        label={t('settings.users.activeAria', { persona: user.display_name })}
        showLabel={false}
        onChange={onToggleActive}
        className="shrink-0"
      />

      {user.role !== 'tech_member' ? (
        // La celda vacía mantiene la grilla: sin ella, la fila de un cliente
        // tendría siete columnas y la de un interno ocho.
        <span aria-hidden="true" />
      ) : (
        <Button
          variant="ghost"
          // Sin saber qué empresas tiene hoy, el panel abriría con todo
          // desmarcado y guardar le SACARÍA las que tenía. Apagado hasta que
          // `GET /users` devuelva `company_ids`.
          disabled={names === null}
          title={names === null ? t(MEMBERSHIPS_UNAVAILABLE_KEY) : undefined}
          onClick={onEditCompanies}
          aria-label={t('settings.users.companiesAria', { persona: user.display_name })}
        >
          {t('common.label.companies')}
        </Button>
      )}
    </li>
  )
}
